import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Pause, Pencil, Play, Plus, Search, Store } from 'lucide-react';
import { MerchantStatus } from '@kick/types';
import { getErrorDetails, getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, formatCOP } from '@/lib/cn';
import { usePanoramaDeCobros, type CobroDeComercio,
  useCambiarPedidoPorLista,
  useCambiarEstadoComercio,
  useActualizarComercioAdmin,
  useCategoriasAdmin,
  useComerciosAdmin,
  useCrearComercio,
  useZonas,
  type ComercioAdmin,
  type Credenciales,
} from '@/lib/queries';
import {
  Boton,
  Campo,
  Cargando,
  Entrada,
  EntradaDinero,
  ErrorCarga,
  Modal,
  Seleccion,
  Vacio,
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { SelectorPunto } from '@/components/SelectorPunto';
import { TarjetaCredenciales } from '@/components/TarjetaCredenciales';
import { CuentaComercio } from '@/components/CuentaComercio';

const ESTADOS: { valor: MerchantStatus; texto: string; color: string }[] = [
  { valor: MerchantStatus.ACTIVE, texto: 'Activo', color: 'bg-emerald-100 text-emerald-800' },
  { valor: MerchantStatus.DRAFT, texto: 'Borrador', color: 'bg-kick-light text-kick-gray' },
  { valor: MerchantStatus.SUSPENDED, texto: 'Suspendido', color: 'bg-red-100 text-red-800' },
  { valor: MerchantStatus.CLOSED, texto: 'Cerrado', color: 'bg-amber-100 text-amber-800' },
];

export function Comercios() {
  const puede = usePuede();
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [creando, setCreando] = useState(false);
  const [credenciales, setCredenciales] = useState<Credenciales | null>(null);
  const [viendoCuenta, setViendoCuenta] = useState<CobroDeComercio | null>(null);

  const comercios = useComerciosAdmin({ q: busqueda || undefined, status: estado || undefined });

  /**
   * La deuda de cada comercio, para el chip de la fila.
   *
   * Es la misma cuenta corrida del módulo de Cobros vista desde aquí: quien
   * está gestionando un comercio ve de una si le debe a la plataforma, sin
   * cambiar de pantalla. Solo se consulta con el permiso de cobros.
   */
  const cobros = usePanoramaDeCobros(puede('cobros', 'ver'));
  const cobroDe = new Map((cobros.data ?? []).map((c) => [c.id, c]));

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Comercios</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Alta, suspensión y comisión de cada local de la plataforma.
          </p>
        </div>
        {puede('comercios', 'crear') && (
          <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setCreando(true)}>
            Nuevo comercio
          </Boton>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-kick-gray" />
          <Entrada
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre"
            aria-label="Buscar comercios"
            className="pl-9"
          />
        </div>
        <Seleccion
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          aria-label="Filtrar por estado"
          className="w-44"
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e.valor} value={e.valor}>
              {e.texto}
            </option>
          ))}
        </Seleccion>
      </div>

      {comercios.isLoading && <Cargando />}
      {comercios.isError && (
        <ErrorCarga
          mensaje={getErrorMessage(comercios.error)}
          onReintentar={() => void comercios.refetch()}
        />
      )}

      {comercios.data?.data.length === 0 && (
        <div className="mt-6">
          <Vacio
            icono={<Building2 className="h-6 w-6" />}
            titulo="Ningún comercio con esos filtros"
            descripcion="Cambia la búsqueda o crea uno nuevo."
          />
        </div>
      )}

      <div className="mt-4 space-y-3">
        {comercios.data?.data.map((comercio) => (
          <Fila
            key={comercio.id}
            comercio={comercio}
            cobro={cobroDe.get(comercio.id)}
            onVerCuenta={() => {
              const cobro = cobroDe.get(comercio.id);
              if (cobro) setViendoCuenta(cobro);
            }}
          />
        ))}
      </div>

      <CuentaComercio comercio={viendoCuenta} onCerrar={() => setViendoCuenta(null)} />

      <ModalNuevo
        abierto={creando}
        onCerrar={() => setCreando(false)}
        onCreado={(c) => {
          setCreando(false);
          if (c) setCredenciales(c);
        }}
      />

      <Modal
        abierto={!!credenciales}
        onCerrar={() => setCredenciales(null)}
        titulo="Comercio creado"
        descripcion="Entrégale estas credenciales al comerciante. No se vuelven a mostrar."
        ancho="sm"
      >
        <div className="p-5">
          {credenciales && <TarjetaCredenciales credenciales={credenciales} />}
          <Boton className="mt-5 w-full" onClick={() => setCredenciales(null)}>
            Ya las copié
          </Boton>
        </div>
      </Modal>
    </div>
  );
}

// ── Fila de comercio ────────────────────────────────────────

function Fila({
  comercio,
  cobro,
  onVerCuenta,
}: {
  comercio: ComercioAdmin;
  /** Su cuenta corrida, si quien mira tiene el permiso de cobros. */
  cobro?: CobroDeComercio;
  onVerCuenta: () => void;
}) {
  const puede = usePuede();
  const avisos = useAvisos();
  const cambiarEstado = useCambiarEstadoComercio();
  const actualizar = useActualizarComercioAdmin();
  const [editandoComision, setEditandoComision] = useState(false);
  const cambiarLista = useCambiarPedidoPorLista();
  const [comision, setComision] = useState(comercio.commissionRate);

  const estado = ESTADOS.find((e) => e.valor === comercio.status);
  const suspendido = comercio.status === MerchantStatus.SUSPENDED;

  const alternarSuspension = (): void => {
    const status = suspendido ? MerchantStatus.ACTIVE : MerchantStatus.SUSPENDED;
    cambiarEstado.mutate(
      { id: comercio.id, status },
      {
        onSuccess: () =>
          avisos.exito(
            suspendido
              ? `${comercio.name} vuelve a la tienda`
              : `${comercio.name} suspendido y oculto de la tienda`,
          ),
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <article className="card-kick flex flex-wrap items-center gap-4 p-4">
      {comercio.logoUrl ? (
        <img src={comercio.logoUrl} alt="" className="h-12 w-12 rounded-kick object-cover" />
      ) : (
        <span className="grid h-12 w-12 place-items-center rounded-kick bg-kick-light text-kick-gray/50">
          <Store className="h-5 w-5" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-kick-ink">{comercio.name}</p>
        <p className="truncate text-sm text-kick-gray">{comercio.addressLine}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('rounded-full px-2 py-0.5 font-semibold', estado?.color)}>
            {estado?.texto ?? comercio.status}
          </span>
          {comercio.isOpenNow && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
              abierto
            </span>
          )}
          <span className="text-kick-gray">
            mínimo {formatCOP(comercio.minOrderAmount)} · {comercio.avgPrepMinutes} min
          </span>
          {cobro && (
            <button
              type="button"
              onClick={onVerCuenta}
              title="La cuenta del comercio con la plataforma: qué debe y registrar su pago o abono."
              className={cn(
                'rounded-full px-2 py-0.5 font-semibold transition hover:opacity-80',
                cobro.deuda > 0
                  ? cobro.vencido
                    ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-900'
                  : 'bg-emerald-50 text-emerald-700',
              )}
            >
              {cobro.deuda > 0 ? `Debe ${formatCOP(cobro.deuda)}` : 'Al día'}
            </button>
          )}
        </div>
      </div>

      <Link
        to={`/comercios/${comercio.id}`}
        className="flex items-center gap-1.5 rounded-kick border border-kick-gray/30 bg-white px-3 py-2 text-sm font-semibold text-kick-ink transition hover:bg-kick-light"
        title="Datos, logo, ubicación, horarios y menú del comercio"
      >
        <Pencil className="h-4 w-4" />
        {puede('comercios', 'editar') ? 'Editar' : 'Ver ficha'}
      </Link>

      {puede('comercios', 'editar') && (
        <Boton
          variante="secundario"
          className={cn(
            'px-3 py-2 text-xs',
            comercio.listOrdering && 'border-indigo-300 bg-indigo-50 text-indigo-800',
          )}
          cargando={cambiarLista.isPending}
          title="Pedido por lista: el cliente escribe su lista y este comercio la cotiza renglón por renglón (para supermercados y droguerías sin catálogo)."
          onClick={() =>
            cambiarLista.mutate(
              { merchantId: comercio.id, listOrdering: !comercio.listOrdering },
              {
                onSuccess: () =>
                  avisos.exito(
                    comercio.listOrdering
                      ? `${comercio.name} vuelve al catálogo normal`
                      : `${comercio.name} ahora recibe pedidos por lista`,
                  ),
                onError: (error) => avisos.error(getErrorMessage(error)),
              },
            )
          }
        >
          {comercio.listOrdering ? 'Por lista: activo' : 'Pedido por lista'}
        </Boton>
      )}

      <div className="text-right">
        <button
          type="button"
          disabled={!puede('comercios', 'editar')}
          onClick={() => setEditandoComision(true)}
          className="rounded-kick px-2 py-1 text-right transition enabled:hover:bg-kick-light disabled:cursor-default"
          title={puede('comercios', 'editar') ? 'Cambiar la comisión de este comercio' : undefined}
        >
          <span className="block text-lg font-bold text-kick-dark">
            {Number(comercio.commissionRate).toFixed(2)}%
          </span>
          <span className="block text-xs text-kick-gray">comisión</span>
        </button>
      </div>

      {puede('comercios', 'bloquear') && (
        <Boton
          variante={suspendido ? 'secundario' : 'peligro'}
          icono={suspendido ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          cargando={cambiarEstado.isPending}
          onClick={alternarSuspension}
        >
          {suspendido ? 'Reactivar' : 'Suspender'}
        </Boton>
      )}

      <Modal
        abierto={editandoComision}
        onCerrar={() => setEditandoComision(false)}
        titulo={`Comisión de ${comercio.name}`}
        descripcion="Porcentaje que retiene la plataforma sobre el subtotal. Solo afecta a pedidos nuevos."
        ancho="sm"
      >
        <div className="p-5">
          <Campo etiqueta="Comisión (%)">
            <Entrada
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={comision}
              onChange={(e) => setComision(e.target.value)}
            />
          </Campo>
          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setEditandoComision(false)}>
              Cancelar
            </Boton>
            <Boton
              cargando={actualizar.isPending}
              onClick={() =>
                actualizar.mutate(
                  { id: comercio.id, commissionRate: Number(comision) },
                  {
                    onSuccess: () => {
                      setEditandoComision(false);
                      avisos.exito('Comisión actualizada');
                    },
                    onError: (error) => avisos.error(getErrorMessage(error)),
                  },
                )
              }
            >
              Guardar
            </Boton>
          </div>
        </div>
      </Modal>
    </article>
  );
}

// ── Alta de comercio ────────────────────────────────────────

function ModalNuevo({
  abierto,
  onCerrar,
  onCreado,
}: {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: (credenciales: Credenciales | null) => void;
}) {
  const avisos = useAvisos();
  const crear = useCrearComercio();
  const categorias = useCategoriasAdmin();
  const zonas = useZonas();

  const [datos, setDatos] = useState({
    name: '',
    categoryId: '',
    addressLine: '',
    phone: '',
    email: '',
    minOrderAmount: 1_000_000,
    avgPrepMinutes: 20,
    zoneId: '',
  });
  const [ubicacion, setUbicacion] = useState<{ lat: number; lng: number } | null>(null);
  const [conDueno, setConDueno] = useState(true);
  const [dueno, setDueno] = useState({ username: '', fullName: '', phone: '', email: '' });

  const errores = crear.error ? getErrorDetails(crear.error) : {};
  const campo = (clave: string): string | undefined => errores[clave]?.[0];

  const enviar = (): void => {
    if (!ubicacion) {
      avisos.error('Ubica el comercio en el mapa: de ahí sale la distancia del domicilio');
      return;
    }

    crear.mutate(
      {
        ...datos,
        location: ubicacion,
        email: datos.email || undefined,
        zoneId: datos.zoneId || undefined,
        ...(conDueno
          ? {
              owner: {
                username: dueno.username,
                fullName: dueno.fullName,
                phone: dueno.phone,
                ...(dueno.email ? { email: dueno.email } : {}),
              },
            }
          : {}),
      },
      {
        onSuccess: (respuesta) => {
          avisos.exito(`${respuesta.comercio.name} creado`);
          onCreado(respuesta.credenciales ?? null);
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Nuevo comercio"
      descripcion="Nace en borrador: revísalo y actívalo cuando esté listo para recibir pedidos."
      ancho="lg"
    >
      <div className="max-h-[70vh] overflow-y-auto p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Nombre" requerido error={campo('name')}>
            <Entrada
              value={datos.name}
              onChange={(e) => setDatos({ ...datos, name: e.target.value })}
              placeholder="Sabor Paisa"
            />
          </Campo>

          <Campo etiqueta="Categoría" requerido error={campo('categoryId')}>
            <Seleccion
              value={datos.categoryId}
              onChange={(e) => setDatos({ ...datos, categoryId: e.target.value })}
            >
              <option value="">Elige una…</option>
              {categorias.data
                ?.filter((c) => c.isActive)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="Dirección" requerido error={campo('addressLine')} className="sm:col-span-2">
            <Entrada
              value={datos.addressLine}
              onChange={(e) => setDatos({ ...datos, addressLine: e.target.value })}
              placeholder="Calle 50 #45-20, La Candelaria"
            />
          </Campo>

          <Campo etiqueta="Teléfono" requerido error={campo('phone')}>
            <Entrada
              value={datos.phone}
              onChange={(e) => setDatos({ ...datos, phone: e.target.value })}
              placeholder="3001234567"
            />
          </Campo>

          <Campo etiqueta="Correo del local" ayuda="Opcional" error={campo('email')}>
            <Entrada
              type="email"
              value={datos.email}
              onChange={(e) => setDatos({ ...datos, email: e.target.value })}
              placeholder="contacto@panaderia.com"
            />
          </Campo>

          <Campo etiqueta="Zona de cobertura" ayuda="Determina la tarifa del domicilio">
            <Seleccion
              value={datos.zoneId}
              onChange={(e) => setDatos({ ...datos, zoneId: e.target.value })}
            >
              <option value="">Sin zona asignada</option>
              {zonas.data?.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="Pedido mínimo">
            <EntradaDinero
              centavos={datos.minOrderAmount}
              onCambio={(v) => setDatos({ ...datos, minOrderAmount: v })}
            />
          </Campo>

          <Campo etiqueta="Preparación media (min)">
            <Entrada
              type="number"
              min={1}
              max={180}
              value={datos.avgPrepMinutes}
              onChange={(e) => setDatos({ ...datos, avgPrepMinutes: Number(e.target.value) })}
            />
          </Campo>
        </div>

        <div className="mt-5">
          <p className="mb-1.5 text-sm font-semibold text-kick-ink">
            Ubicación en el mapa <span className="text-kick-red">*</span>
          </p>
          <p className="mb-2 text-xs text-kick-gray">
            Es el punto de recogida del domiciliario y el origen de la distancia que se le cobra al
            cliente.
          </p>
          <SelectorPunto valor={ubicacion} onCambio={setUbicacion} />
        </div>

        <div className="mt-6 rounded-kick border border-kick-gray/20 p-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={conDueno}
              onChange={(e) => setConDueno(e.target.checked)}
              className="h-4 w-4 accent-kick-red"
            />
            <span className="font-semibold text-kick-ink">Crear también su usuario propietario</span>
          </label>
          <p className="mt-1 text-xs text-kick-gray">
            Se genera una contraseña temporal que se muestra una sola vez. El comerciante está
            obligado a cambiarla en su primer ingreso.
          </p>

          {conDueno && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Usuario" requerido error={campo('owner.username')}>
                <Entrada
                  value={dueno.username}
                  onChange={(e) => setDueno({ ...dueno, username: e.target.value.toLowerCase() })}
                  placeholder="saborpaisa"
                />
              </Campo>
              <Campo etiqueta="Nombre completo" requerido error={campo('owner.fullName')}>
                <Entrada
                  value={dueno.fullName}
                  onChange={(e) => setDueno({ ...dueno, fullName: e.target.value })}
                  placeholder="Propietario Sabor Paisa"
                />
              </Campo>
              <Campo etiqueta="Teléfono" requerido error={campo('owner.phone')}>
                <Entrada
                  value={dueno.phone}
                  onChange={(e) => setDueno({ ...dueno, phone: e.target.value })}
                  placeholder="3009998877"
                />
              </Campo>
              <Campo etiqueta="Correo" ayuda="Opcional, para recuperar la contraseña">
                <Entrada
                  type="email"
                  value={dueno.email}
                  onChange={(e) => setDueno({ ...dueno, email: e.target.value })}
                />
              </Campo>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton cargando={crear.isPending} onClick={enviar}>
            Crear comercio
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
